import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import axios from 'axios';
import { MESSAGES } from '../common/messages';

import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { enqueueSnackbar } from 'notistack';

const EditSelfCheckItems = (props) => {
  const { typography, palette } = useTheme();
  const MAX_ITEM_COUNT = 10;

  // セルフチェック問題を保持する変数
  const [selfCheckItems, setSelfCheckItems] = useState([]);

  // 末尾にセルフチェック問題を追加
  const addItem = () => {
    const copied = selfCheckItems.concat();
    copied.push({
      key: Math.random(),
      text: '',
      serialNumber: selfCheckItems.length,
      choices: [
        { serialNumber: 0, text: '', key: Math.random() },
        { serialNumber: 1, text: '', key: Math.random() },
        { serialNumber: 2, text: '', key: Math.random() }],
    });
    setSelfCheckItems(copied);
  };

  const styles = {
    main: {
      display: 'flex',
      padding: '0px 24px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      alignSelf: 'stretch',

      borderRadius: '8px',
      border: '1px solid ' + palette.divider,
      background: palette.white,
      boxShadow: '2px 2px 2px 0px rgba(0, 0, 0, 0.03)',
      margin: '0px auto 0px auto',
    },
    backButton: {
      ...typography.body14bold,
      display: 'flex',
      width: '232px',
      height: '44px',
      padding: '0px 16px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      margin: '0px 8px 0px 8px',

      borderRadius: '100px',
      border: '1px solid ' + palette.primary.light,
      background: palette.white,
      color: palette.primary.light,
    },
    updateButton: {
      ...typography.body14bold,
      'width': '232px',
      'height': '44px',
      'padding': '0px 16px',
      'justifyContent': 'center',
      'alignItems': 'center',
      'gap': '8px',
      'margin': '0px 8px 0px 8px',

      'borderRadius': '100px',
      'border': '1px solid ' + palette.primary.light,
      'background': palette.primary.light,
      'color': palette.white,
      '&:hover': {
        width: '232px',
        height: '44px',
        padding: '0px 16px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        margin: '0px 8px 0px 8px',
        borderRadius: '100px',
        border: '1px solid ' + palette.primary.light,
        background: palette.primary.light,
        color: palette.white,
      },
      '&:disabled': {
        ...typography.body14bold,
        border: '1px solid ' + palette.disabledCccccc,
        color: palette.white,
        background: palette.disabledCccccc,
      },
    },
    addItemButton: {
      ...typography.body14bold,
      width: '238px',
      height: '44px',
      backgroundColor: 'white',
      color: palette.primary.main,
      border: '1px solid ' + palette.primary.main,
      margin: '44px 0px',
    },
  };

  useEffect(() => {
    const fetchSelfCheckItems = async () => {
      try {
        const result = await axios.get('/v1/self-check-items');
        const data = result.data.items.map((e) => {
          const item = {
            serialNumber: e.serialNumber,
            text: e.text,
            choices: e.choices.map((c) => {
              c.key = Math.random();
              return c;
            }),
            key: Math.random(),
          };
          e.choices.forEach((c) => {
            if (c._id === e.correctAnswerId) {
              item.correctAnswerSerialNumber = c.serialNumber;
            }
          });
          return item;
        });
        if (data.length === 0) {
          addItem();
        } else {
          setSelfCheckItems(data);
        }
      } catch (err) { }
    };
    fetchSelfCheckItems();
  }, []);

  // セルフチェック問題を一問を削除する関数
  const removeSelfCheckItem = (serialNumber) => {
    const copied = selfCheckItems.concat();
    for (let i = serialNumber; i < copied.length - 1; i++) {
      copied[i] = copied[i + 1];
      copied[i].serialNumber = i;
    }
    copied.pop();
    setSelfCheckItems(copied);
  };

  // 未入力・未選択の項目があるか
  const isInsufficient = (items) => {
    // 問題文に空欄があるか
    if (items.some((item) => item.text.length === 0)) {
      return true;
    }
    // 選択肢の文に空欄があるか
    let isInsufficient = false;
    items.forEach((item) =>
      isInsufficient = item.choices.some((choice) => choice.text.length === 0),
    );
    if (isInsufficient) {
      return true;
    }
    // 正解が選択されていない問題があるか
    return items.some((item) => item.correctAnswerSerialNumber === undefined);
  };

  const handleSubmit = async () => {
    if (isInsufficient(selfCheckItems)) {
      enqueueSnackbar(MESSAGES.INSUFFICIENT, { variant: 'error' });
      return;
    }

    // 登録APIを呼び出し
    try {
      await axios.post('/v1/self-check-items', selfCheckItems);
      enqueueSnackbar(MESSAGES.SELF_CHECK_ITEMS_UPDATED);
      props.handleMoveToTrainingResult();
    } catch (err) { }
  };

  return (
    <Container sx={{ margin: '16px auto' }}>
      <Stack direction='row' sx={styles.main}>
        <Typography variant='head20' margin='24px 0px 0px 0px'>受講者セルフチェック問題の編集</Typography>
        {selfCheckItems.map((e) => {
          return (
            <SelfCheckItemComponent
              key={e.key}
              selfCheckItem={e}
              questionNumber={e.serialNumber + 1}
              setSelfCheckItem={(input) => {
                const copied = selfCheckItems.filter((item) => item.serialNumber !== e.serialNumber);
                copied.push(input);
                copied.sort((a, b) => a.serialNumber - b.serialNumber);
                setSelfCheckItems(copied);
              }}
              removeSelfCheckItem={removeSelfCheckItem}
              numberOfItems={selfCheckItems.length}
            />
          );
        })}
        <Button variant="outlined"
          sx={styles.addItemButton}
          startIcon={<AddIcon />}
          onClick={() => {
            addItem();
          }}
          disabled={selfCheckItems.length === MAX_ITEM_COUNT}
        >
          問題を追加する（残り{MAX_ITEM_COUNT - selfCheckItems.length}問）
        </Button>

        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction={'row'} sx={{ width: 'fit-content', margin: '0px auto 24px auto' }}>
            <Button variant="outlined"
              sx={styles.backButton}
              onClick={() => {
                props.handleMoveToTrainingResult();
              }}
            >
              <p>トレーニング結果一覧に戻る</p>
            </Button>
            <Button variant="filled"
              sx={styles.updateButton}
              disabled={isInsufficient(selfCheckItems)}
              onClick={handleSubmit}
            >
              <p>問題を更新する</p>
            </Button>
          </Stack>
        </Grid>
      </Stack>
    </Container>
  );
};

const SelfCheckItemComponent = (props) => {
  const { typography, palette } = useTheme();

  const styles = {
    main: {
      display: 'flex',
      padding: '24px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '36px',
      alignSelf: 'stretch',

      borderRadius: '8px',
      border: '1px solid ' + palette.divider,
      background: palette.white,
      boxShadow: '2px 2px 2px 0px rgba(0, 0, 0, 0.03)',
    },
    deleteButton: {
      display: 'flex',
      height: '44px',
      alignItems: 'center',
      gap: '4px',

      width: '154px',
      padding: '0px 0px 0px 0px',
      alignItems: 'center',
      margin: '24px 0px 0px 0px',

      /* Style */
      borderRadius: '8px',

      ...typography.body14bold,
    },
    deleteIcon: {
      ...typography.body14,
      display: 'flex',
      width: '24px',
      height: '24px',
      justifyContent: 'center',
      alignItems: 'center',
    },
  };

  return (
    <Stack sx={{ width: '100%' }}>
      <Typography variant='body16bold' margin='44px 0px 0px 0px'>問{props.questionNumber}</Typography>
      <TextField variant="outlined"
        placeholder="問題文"
        InputProps={{
          style: {
            backgroundColor: 'white',
          },
        }}
        multiline
        maxRows={3}
        defaultValue={props.selfCheckItem.text}
        onChange={(event) => {
          const copied = structuredClone(props.selfCheckItem);
          copied.text = event.target.value;
          props.setSelfCheckItem(copied);
        }}
      />
      <p style={{ ...typography.body16bold, margin: '24px 0px 16px 0px' }}>正解と選択肢</p>
      <RadioGroup onChange={(event) => {
        // 選択されたラジオボタンが変化した場合の処理
        const copied = structuredClone(props.selfCheckItem);
        copied.correctAnswerSerialNumber = Number(event.target.value);
        props.setSelfCheckItem(copied);
      }}>
        {
          props.selfCheckItem.choices.map((e) => {
            return (<Stack direction='row' sx={{ width: '100%' }} key={e.key}>
              <Radio
                value={e.serialNumber}
                checked={(props.selfCheckItem.correctAnswerSerialNumber === e.serialNumber)}
                sx={{ color: palette.primary.main }}></Radio>
              <TextField variant="outlined"
                placeholder={(e.serialNumber + 1) + 'つ目の選択肢'}
                InputProps={{
                  style: {
                    backgroundColor: 'white',
                  },
                }}
                defaultValue={e.text}
                sx={{ padding: '8px 0px 8px 0px', width: '100%' }}
                multiline
                maxRows={3}
                onChange={(event) => {
                  const copied = structuredClone(props.selfCheckItem);
                  copied.choices[e.serialNumber].text = event.target.value;
                  props.setSelfCheckItem(copied);
                }}
              />
            </Stack>);
          })
        }
      </RadioGroup>
      {props.numberOfItems > 1 && <Button variant="outlined" color='error'
        sx={styles.deleteButton}
        startIcon={<DeleteIcon sx={styles.deleteIcon} />}
        onClick={() => {
          // button click action
          props.removeSelfCheckItem(props.selfCheckItem.serialNumber);
        }}
      >
        問題を削除する
      </Button>}
    </Stack>
  );
};

export default EditSelfCheckItems;

SelfCheckItemComponent.propTypes = {
  selfCheckItem: PropTypes.object,
  questionNumber: PropTypes.number,
  removeSelfCheckItem: PropTypes.func,
  setSelfCheckItem: PropTypes.func,
  numberOfItems: PropTypes.number,
};

EditSelfCheckItems.propTypes = {
  handleMoveToTrainingResult: PropTypes.func,
};

